import React, { useMemo, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Button,
  Chip,
  Tab,
  Tabs,
  Autocomplete,
  TextField,
  Skeleton,
  alpha,
  useTheme,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Avatar,
  useMediaQuery,
} from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanbanOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useEffectivePermissions, scopeSetFromPermissions } from "../../hooks/useEffectivePermissions";
import { useAuthContext } from "../../context/AuthContext";
import {
  useTeamBoardTasks,
  TEAM_BOARD_TASKS_KEY,
  teamBoardKey,
} from "../../hooks/useTeamBoards";
import { useUsers } from "../../hooks/useUsers";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { moveTeamBoardCard } from "../../api/client";
import { canExecuteTeamTask, hasGlobalTeamTasksManage } from "./utils/teamBoardPermissions";
import TaskColumnActions from "./components/TaskColumnActions";
import { openTeamTasksPopupWindow } from "../../utils/openTeamTasksPopupWindow";
import { formatTaskDisplayName } from "./utils/taskDisplayName";
import TaskCardModalContainer from "./components/TaskCardModalContainer";
import TeamTasksLoadError from "./components/TeamTasksLoadError";
import { MyTasksAnalytics } from "./components/TaskAnalyticsPanels";
import TaskStageTimeBar from "./components/TaskStageTimeBar";
import { TeamTasksFeedbackProvider } from "./components/TeamTasksFeedbackProvider";
import { useTeamBoardAction } from "./hooks/useTeamBoardAction";
import { getInitials } from "../../utils/utils";
import { TEAM_MOBILE_MEDIA_QUERY } from "../Team/utils/teamUiUtils";
import {
  DUE_FILTERS,
  NO_CLIENT_ID,
  UNASSIGNED_ASSIGNEE_ID,
  formatDeletedUserName,
} from "./utils/teamBoardCardFilters";

function formatDueChip(dueAt) {
  if (!dueAt) return null;
  const d = new Date(dueAt);
  if (Number.isNaN(d.getTime())) return null;
  const overdue = d < new Date();
  return {
    label: d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }),
    overdue,
  };
}

function sortOtherInboxTasks(a, b) {
  const aDue = a.dueAt ? new Date(a.dueAt).getTime() : null;
  const bDue = b.dueAt ? new Date(b.dueAt).getTime() : null;
  if (aDue != null && bDue != null) return aDue - bDue;
  if (aDue != null) return -1;
  if (bDue != null) return 1;
  return String(a.title || "").localeCompare(String(b.title || ""), "pt-BR");
}

function isWaitingTask(task) {
  return Boolean(task?.columnIsWaiting) && !task?.columnIsDone && !task?.completedAt;
}

function groupTasksByDue(tasks, { deferWaiting = false } = {}) {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endOfToday = new Date(startOfToday);
  endOfToday.setDate(endOfToday.getDate() + 1);
  const endWeek = new Date(startOfToday);
  endWeek.setDate(endWeek.getDate() + 7);

  const groups = {
    overdue: [],
    today: [],
    week: [],
    other: [],
    waiting: [],
    done: [],
  };

  for (const task of tasks) {
    if (deferWaiting && isWaitingTask(task)) {
      groups.waiting.push(task);
      continue;
    }
    if (task.columnIsDone || task.completedAt) {
      groups.done.push(task);
      continue;
    }
    if (!task.dueAt) {
      groups.other.push(task);
      continue;
    }
    const d = new Date(task.dueAt);
    if (d < startOfToday) groups.overdue.push(task);
    else if (d < endOfToday) groups.today.push(task);
    else if (d < endWeek) groups.week.push(task);
    else groups.other.push(task);
  }

  groups.other.sort(sortOtherInboxTasks);
  groups.waiting.sort(sortOtherInboxTasks);
  return groups;
}

function InboxAssigneeBadge({ names = [], people = [], t }) {
  const theme = useTheme();
  const labels = people.length
    ? people.map((person) =>
        formatDeletedUserName(person?.nome || person?.name, person?.deleted, t("deletedUserMark"))
      ).filter(Boolean)
    : names;
  const label = labels.length ? labels.join(", ") : t("inboxUnassignedLabel");
  const primaryName = labels[0] || "";

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={{
        minWidth: 0,
        maxWidth: "100%",
        px: 0.75,
        py: 0.35,
        borderRadius: 999,
        bgcolor: alpha(theme.palette.action.active, labels.length ? 0.06 : 0.04),
      }}
    >
      <Avatar
        sx={{
          width: 22,
          height: 22,
          fontSize: "0.62rem",
          fontWeight: 700,
          bgcolor: labels.length ? "primary.main" : alpha(theme.palette.text.primary, 0.12),
          color: labels.length ? "primary.contrastText" : "text.secondary",
        }}
      >
        {labels.length ? getInitials(primaryName) : <PersonOutlineIcon sx={{ fontSize: 14 }} />}
      </Avatar>
      <Typography
        variant="caption"
        noWrap
        sx={{
          fontWeight: 600,
          fontStyle: labels.length ? "normal" : "italic",
          color: labels.length ? "text.primary" : "text.secondary",
          minWidth: 0,
        }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

function InboxTaskCard({ task, showAssignees, showBoard, showColumnStatus, onOpen, onMove, canExecute, t }) {
  const theme = useTheme();
  const due = formatDueChip(task.dueAt);
  const durationLabel = task.estimatedDuration
    ? String(task.estimatedDuration).slice(0, 5)
    : null;
  const waiting = Boolean(task.columnIsWaiting) && !task.columnIsDone;
  const columnChipVisible = Boolean(task.columnName) && (!canExecute || showColumnStatus);
  const waitingHint = t("inboxWaitingStageHint", {
    column: task.columnName ? `: ${task.columnName}` : "",
  });

  return (
    <Card
      elevation={0}
      onClick={() => onOpen(task)}
      sx={{
        cursor: "pointer",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: task.columnIsDone
          ? alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.85 : 0.92)
          : waiting
            ? alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.1 : 0.06)
            : "background.paper",
        border: `1px solid ${
          waiting
            ? alpha(theme.palette.warning.main, 0.45)
            : alpha(theme.palette.divider, task.columnIsDone ? 0.45 : 0.65)
        }`,
        borderLeft: waiting ? `3px solid ${theme.palette.warning.main}` : undefined,
        boxShadow:
          theme.palette.mode === "dark"
            ? "none"
            : "0 1px 2px rgba(15,23,42,0.04)",
        opacity: task.columnIsDone ? 0.9 : 1,
        transition: "box-shadow 0.15s, border-color 0.15s, transform 0.1s, opacity 0.15s",
        "&:hover": {
          opacity: 1,
          borderColor: "primary.light",
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 0 0 1px ${theme.palette.primary.main}`
              : "0 4px 12px rgba(15,23,42,0.08)",
        },
        "&:active": { transform: "scale(0.995)" },
      }}
    >
      <CardContent sx={{ p: 1.35, "&:last-child": { pb: 1.35 } }}>
        {(showBoard && task.boardName) || (task.columnName && !canExecute) ? (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
            {showBoard && task.boardName ? (
              <Chip
                icon={<ViewKanbanOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                label={task.boardName}
                size="small"
                variant="outlined"
                sx={{
                  height: 24,
                  fontWeight: 600,
                  maxWidth: "100%",
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            ) : null}
            {columnChipVisible ? (
              <Chip
                icon={
                  waiting ? (
                    <HourglassEmptyOutlinedIcon sx={{ fontSize: "14px !important" }} />
                  ) : undefined
                }
                label={task.columnName}
                size="small"
                title={waiting ? waitingHint : undefined}
                variant={task.columnIsDone || waiting ? "filled" : "outlined"}
                color={task.columnIsDone ? "success" : waiting ? "warning" : "default"}
                sx={{ height: 24, fontWeight: 600 }}
              />
            ) : null}
          </Stack>
        ) : null}

        <Stack direction="row" alignItems="flex-start" spacing={0.75} sx={{ minWidth: 0 }}>
          {task.isRequired ? (
            <Tooltip title={t("cardRequiredHint", { defaultValue: "Tarefa obrigatória" })}>
              <FlagOutlinedIcon
                sx={{ fontSize: 16, color: "warning.main", mt: 0.15, flexShrink: 0 }}
                aria-hidden
              />
            </Tooltip>
          ) : null}
          {task.linkedCompletionPendingReview ? (
            <Tooltip title={t("linkedCompletionPendingReviewHint")}>
              <WarningAmberOutlinedIcon
                sx={{ fontSize: 16, color: "warning.main", mt: 0.15, flexShrink: 0 }}
                aria-label={t("linkedCompletionPendingReview")}
              />
            </Tooltip>
          ) : null}
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              minWidth: 0,
              fontWeight: 400,
              fontSize: "0.8125rem",
              lineHeight: 1.5,
              wordBreak: "break-word",
              color: task.columnIsDone ? "text.secondary" : (th) => alpha(th.palette.text.primary, 0.9),
              textDecoration: task.columnIsDone ? "line-through" : "none",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {formatTaskDisplayName(task.id, task.title)}
          </Typography>
        </Stack>
        {task.linkedCompletionPendingReview ? (
          <Chip
            size="small"
            label={t("linkedCompletionPendingReview")}
            color="warning"
            variant="outlined"
            sx={{ mt: 0.75, height: 22, fontWeight: 700, fontSize: "0.68rem" }}
          />
        ) : null}
        {task.clientName ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5, fontWeight: 600 }}
          >
            {task.clientName}
          </Typography>
        ) : null}

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ mt: 1.15, minWidth: 0, flexWrap: "wrap", gap: 0.75 }}
        >
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
            {due ? (
              <Chip
                icon={
                  task.columnIsDone ? (
                    <CheckCircleOutlineIcon sx={{ fontSize: "14px !important" }} />
                  ) : (
                    <ScheduleOutlinedIcon sx={{ fontSize: "14px !important" }} />
                  )
                }
                label={due.label}
                size="small"
                color={task.columnIsDone ? "success" : due.overdue ? "error" : "default"}
                variant={task.columnIsDone || due.overdue ? "filled" : "outlined"}
                sx={{ fontWeight: 600, height: 24 }}
              />
            ) : (
              <Chip
                icon={<ScheduleOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                label={t("inboxNoDueLabel")}
                size="small"
                variant="outlined"
                sx={{
                  height: 24,
                  fontWeight: 600,
                  color: "text.secondary",
                  borderStyle: "dashed",
                  fontStyle: "italic",
                }}
              />
            )}
            {waiting && !columnChipVisible ? (
              <Tooltip title={waitingHint}>
                <Chip
                  icon={<HourglassEmptyOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                  label={task.columnName || t("inboxWaitingStage")}
                  size="small"
                  color="warning"
                  variant="filled"
                  sx={{ fontWeight: 700, height: 24, maxWidth: "100%" }}
                />
              </Tooltip>
            ) : null}
            {durationLabel ? (
              <Chip
                icon={<AccessTimeOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                label={durationLabel}
                size="small"
                variant="outlined"
                title={t("cardEstimatedDuration")}
                sx={{ fontWeight: 600, height: 24 }}
              />
            ) : null}
            {showAssignees ? (
              <Box sx={{ minWidth: 0, maxWidth: { xs: "100%", sm: "48%" } }}>
                <InboxAssigneeBadge names={task.assigneeNames} people={task.assigneeUsers} t={t} />
              </Box>
            ) : null}
          </Stack>

          {canExecute ? (
            <TaskColumnActions
              boardId={task.boardId}
              cardId={task.id}
              columnId={task.columnId}
              columnIsDone={task.columnIsDone}
              onMove={(columnId) => onMove(task, columnId)}
              compact
            />
          ) : null}
        </Stack>
      </CardContent>
      {(task.stageTime || []).length > 0 ? (
        <TaskStageTimeBar
          stageTime={task.stageTime}
          totalSec={task.stageTimeTotalSec}
          height={3}
        />
      ) : null}
    </Card>
  );
}

function TaskGroupSection({ title, subtitle, tone, tasks, showAssignees, showBoard, showColumnStatus, onOpenTask, onMoveTask, canExecuteTask, t }) {
  const theme = useTheme();
  if (!tasks.length) return null;

  const toneColor =
    tone === "error"
      ? theme.palette.error
      : tone === "warning"
        ? theme.palette.warning
        : tone === "success"
          ? theme.palette.success
          : null;

  return (
    <Box
      sx={{
        minWidth: 0,
        pl: title ? 1.75 : 0,
        borderLeft: title ? "3px solid" : "none",
        borderColor: toneColor
          ? alpha(toneColor.main, theme.palette.mode === "dark" ? 0.65 : 0.45)
          : alpha(theme.palette.divider, 0.9),
      }}
    >
      {title ? (
        <Box sx={{ mb: 1.25 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="subtitle2"
              fontWeight={800}
              sx={{ color: toneColor ? `${tone}.main` : "text.primary" }}
            >
              {title}
            </Typography>
            <Chip
              size="small"
              label={tasks.length}
              color={toneColor ? tone : "default"}
              variant={toneColor ? "filled" : "outlined"}
              sx={{ fontWeight: 700, height: 22, minWidth: 28 }}
            />
          </Stack>
          {subtitle ? (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.35, display: "block" }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      ) : null}
      <Stack spacing={1}>
        {tasks.map((task) => (
          <InboxTaskCard
            key={task.id}
            task={task}
            showAssignees={showAssignees}
            showBoard={showBoard}
            showColumnStatus={showColumnStatus}
            onOpen={onOpenTask}
            onMove={onMoveTask}
            canExecute={canExecuteTask(task)}
            t={t}
          />
        ))}
      </Stack>
    </Box>
  );
}

function InboxSkeleton() {
  return (
    <Stack spacing={3} sx={{ width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}>
      {[0, 1].map((group) => (
        <Box key={group}>
          <Skeleton variant="text" width={140} height={28} sx={{ mb: 1.25 }} />
          <Stack spacing={1}>
            {[0, 1].map((card) => (
              <Skeleton key={card} variant="rounded" height={108} sx={{ borderRadius: 2 }} />
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

const TasksInboxPageInner = ({ standalone = false, basePath = "/equipe/tarefas" }) => {
  const { t, i18n } = useTranslation("teamTasks");
  const teamTasksLabelsReady = i18n.hasResourceBundle(
    i18n.resolvedLanguage || i18n.language || "pt-BR",
    "teamTasks"
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(TEAM_MOBILE_MEDIA_QUERY);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { run, showSuccess } = useTeamBoardAction();
  const [searchParams, setSearchParams] = useSearchParams();

  const { userData } = useAuthContext();
  const { data: permissionsData } = useEffectivePermissions();
  const scopes = scopeSetFromPermissions(permissionsData, userData);
  const canManageTeam = hasGlobalTeamTasksManage(scopes);

  const scopeParam = (() => {
    const raw = searchParams.get("scope");
    if (raw === "team" && canManageTeam) return "team";
    if (raw === "created") return "created";
    if (raw === "analytics") return "analytics";
    return "me";
  })();
  const statusParam = ["open", "done", "all"].includes(searchParams.get("status"))
    ? searchParams.get("status")
    : "open";
  const overdueOnly = searchParams.get("overdue") === "1";
  const dueParamRaw = searchParams.get("due");
  const dueParam =
    !overdueOnly && Object.values(DUE_FILTERS).includes(dueParamRaw) && dueParamRaw !== DUE_FILTERS.OVERDUE
      ? dueParamRaw
      : null;
  const requiredOnly = searchParams.get("required") === "1";
  const assigneeIdParam = searchParams.get("assigneeId");
  const assigneeId =
    assigneeIdParam != null &&
    assigneeIdParam !== "" &&
    Number.isFinite(Number(assigneeIdParam))
      ? Number(assigneeIdParam)
      : null;
  const clientIdParam = searchParams.get("clientId");
  const clientId =
    clientIdParam != null &&
    clientIdParam !== "" &&
    Number.isFinite(Number(clientIdParam))
      ? Number(clientIdParam)
      : null;

  const filters = useMemo(
    () => ({
      scope: scopeParam,
      status: statusParam,
      overdue: overdueOnly,
      due: dueParam || undefined,
      required: requiredOnly || undefined,
      assigneeId:
        (scopeParam === "team" || scopeParam === "created") && assigneeId != null
          ? assigneeId
          : undefined,
      clientId: clientId != null ? clientId : undefined,
    }),
    [scopeParam, statusParam, overdueOnly, dueParam, requiredOnly, assigneeId, clientId]
  );

  const isAnalytics = scopeParam === "analytics";
  const { data, isLoading, isError, error, refetch, isFetching } = useTeamBoardTasks(filters, {
    enabled: !isAnalytics,
  });
  const tasks = data?.tasks || [];
  const stats = useMemo(() => {
    const base = data?.stats || {};
    if (scopeParam !== "me") return base;
    const now = new Date();
    const actionable = tasks.filter((task) => !isWaitingTask(task));
    return {
      ...base,
      open: actionable.filter((task) => !task.columnIsDone && !task.completedAt).length,
      overdue: actionable.filter((task) => {
        if (!task.dueAt || task.columnIsDone || task.completedAt) return false;
        const due = new Date(task.dueAt);
        return !Number.isNaN(due.getTime()) && due < now;
      }).length,
    };
  }, [data?.stats, scopeParam, tasks]);

  const { data: usersData } = useUsers(1, 200, "", "humanos");
  const users = usersData?.users || usersData || [];

  const unassignedAssigneeOption = useMemo(
    () => ({ id: UNASSIGNED_ASSIGNEE_ID, nome: t("inboxFilterUnassigned") }),
    [t]
  );
  const assigneeOptions = useMemo(
    () => [unassignedAssigneeOption, ...users],
    [unassignedAssigneeOption, users]
  );

  const noClientOption = useMemo(
    () => ({ id: NO_CLIENT_ID, name: t("inboxFilterNoClient") }),
    [t]
  );
  const clientOptions = useMemo(() => {
    const byId = new Map();
    for (const task of tasks) {
      const id = task?.clientId != null && Number(task.clientId) > 0 ? Number(task.clientId) : null;
      if (!id || byId.has(id)) continue;
      byId.set(id, {
        id,
        name: String(task.clientName || "").trim() || `Cliente #${id}`,
      });
    }
    if (clientId != null && clientId > 0 && !byId.has(clientId)) {
      byId.set(clientId, { id: clientId, name: `Cliente #${clientId}` });
    }
    return [noClientOption, ...[...byId.values()].sort((a, b) => a.name.localeCompare(b.name, "pt-BR"))];
  }, [tasks, clientId, noClientOption]);

  const [selectedTask, setSelectedTask] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const canExecuteTask = useCallback(
    (task) => {
      if (scopeParam === "me") return true;
      return canExecuteTeamTask(scopes, null, task, userData?.id);
    },
    [scopeParam, scopes, userData?.id]
  );

  const handleMoveTask = async (task, columnId, { successKey = "successCardMoved" } = {}) => {
    if (!task?.boardId || !columnId) return false;
    const wasDone = task.columnIsDone;

    const result = await run(async () => {
      const payload = await moveTeamBoardCard(task.boardId, task.id, { columnId, position: 0 });
      return payload;
    }, { context: "moveCard" });

    if (result == null) return false;

    let willBeDone = wasDone;
    let willBeWaiting = Boolean(task.columnIsWaiting);
    let targetCol = null;
    for (const col of result?.columns || []) {
      if (col.id === columnId) targetCol = col;
      if ((col.cards || []).some((c) => c.id === task.id)) {
        willBeDone = Boolean(col.isDone);
        willBeWaiting = Boolean(col.isWaiting);
        targetCol = col;
      }
    }
    if (targetCol) willBeWaiting = Boolean(targetCol.isWaiting);

    let key = successKey;
    if (!wasDone && willBeDone) key = "successCardCompleted";
    else if (wasDone && !willBeDone) key = "successCardReopened";
    else if (columnId !== task.columnId) key = "successCardMoved";
    showSuccess(t(key));

    queryClient.invalidateQueries(TEAM_BOARD_TASKS_KEY);
    queryClient.invalidateQueries(teamBoardKey(task.boardId));

    if (selectedTask?.id === task.id) {
      setSelectedTask((prev) =>
        prev
          ? {
              ...prev,
              columnId,
              columnIsDone: willBeDone,
              columnIsWaiting: willBeWaiting,
              columnName: targetCol?.name || prev.columnName,
            }
          : prev
      );
    }
    return true;
  };

  const groups = useMemo(
    () => groupTasksByDue(tasks, { deferWaiting: scopeParam === "me" }),
    [tasks, scopeParam]
  );
  const showAssignees = scopeParam === "team" || scopeParam === "created";
  const showBoard = true;
  const showColumnStatus = scopeParam === "created";

  const inboxCopy = useMemo(() => {
    if (scopeParam === "team") {
      return {
        title: t("inboxTitleTeam"),
        intro: t("inboxIntroTeam"),
        empty: t("inboxEmptyTeam"),
      };
    }
    if (scopeParam === "created") {
      return {
        title: t("inboxTitleCreated"),
        intro: t("inboxIntroCreated"),
        empty: t("inboxEmptyCreated"),
      };
    }
    if (scopeParam === "analytics") {
      return {
        title: t("inboxTabAnalytics"),
        intro: t("inboxAnalyticsHint"),
        empty: "",
      };
    }
    return {
      title: t("inboxTitleMine"),
      intro: t("inboxIntroMine"),
      empty: t("inboxEmptyMine"),
    };
  }, [scopeParam, t, teamTasksLabelsReady]);

  const inboxSections = useMemo(() => {
    const urgentSections = [
      { key: "overdue", title: t("inboxGroupOverdue"), tone: "error", tasks: groups.overdue },
      { key: "today", title: t("inboxGroupToday"), tone: "warning", tasks: groups.today },
      { key: "week", title: t("inboxGroupWeek"), tasks: groups.week },
    ].filter((section) => section.tasks.length > 0);

    const sections = [...urgentSections];

    if (groups.other.length > 0) {
      if (urgentSections.length > 0) {
        sections.push({
          key: "other",
          title: t("inboxGroupOther"),
          subtitle: t("inboxGroupOtherHint"),
          tasks: groups.other,
        });
      } else {
        sections.push({
          key: "other-flat",
          tasks: groups.other,
        });
      }
    }

    if (groups.waiting.length > 0) {
      sections.push({
        key: "waiting",
        title: t("inboxWaitingStage"),
        subtitle: t("inboxWaitingStageListHint"),
        tasks: groups.waiting,
      });
    }

    if (groups.done.length > 0) {
      sections.push({
        key: "done",
        title: t("inboxGroupDone"),
        tone: "success",
        tasks: groups.done,
      });
    }

    return sections;
  }, [groups, t, teamTasksLabelsReady]);

  const setScope = (scope) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (scope === "me") next.delete("scope");
      else next.set("scope", scope);
      if (scope !== "team" && scope !== "created") next.delete("assigneeId");
      return next;
    }, { replace: true });
  };

  const patchSearchParams = (mutator) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      mutator(next);
      return next;
    }, { replace: true });
  };

  const setRequiredFilter = (on) => {
    patchSearchParams((next) => {
      if (on) next.set("required", "1");
      else next.delete("required");
    });
  };

  const setAssigneeFilter = (user) => {
    patchSearchParams((next) => {
      if (user != null && user.id != null && user.id !== "") {
        next.set("assigneeId", String(user.id));
      } else {
        next.delete("assigneeId");
      }
    });
  };

  const setClientFilter = (client) => {
    patchSearchParams((next) => {
      if (client != null && client.id != null && client.id !== "") {
        next.set("clientId", String(client.id));
      } else {
        next.delete("clientId");
      }
    });
  };

  const setOpenStatusFilter = ({ overdue = false, due = null } = {}) => {
    patchSearchParams((next) => {
      next.delete("status");
      if (overdue) {
        next.set("overdue", "1");
        next.delete("due");
      } else {
        next.delete("overdue");
        if (due) next.set("due", String(due));
        else next.delete("due");
      }
    });
  };

  const handleOpenTask = (task) => {
    setSelectedTask(task);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedTask(null);
  };

  const selectedUser = useMemo(() => {
    if (assigneeId === UNASSIGNED_ASSIGNEE_ID) return unassignedAssigneeOption;
    if (assigneeId == null) return null;
    return users.find((u) => Number(u.id) === assigneeId) || null;
  }, [users, assigneeId, unassignedAssigneeOption]);

  const selectedClient = useMemo(() => {
    if (clientId == null) return null;
    return clientOptions.find((c) => Number(c.id) === clientId) || null;
  }, [clientOptions, clientId]);

  const showAssigneeFilter = scopeParam === "team" || scopeParam === "created";

  const boardsPath = basePath;
  const showOpenInPopup = !standalone && !isMobile;
  const showBackToBoards = !isMobile || standalone;

  return (
    <Box
      sx={{
        ...(standalone
          ? {
              height: "100vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }
          : {
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              overflowX: "hidden",
            }),
        bgcolor: alpha(theme.palette.background.default, 0.4),
      }}
    >
      {standalone ? (
        <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, pt: 1.25, flexShrink: 0 }}>
          <Tooltip title={t("closeWindow")}>
            <IconButton size="small" onClick={() => window.close()} aria-label={t("closeWindow")}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ) : null}

      <Box
        sx={{
          ...(standalone
            ? {
                flex: 1,
                minHeight: 0,
                overflowX: "hidden",
                overflowY: "auto",
              }
            : {
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
              }),
          boxSizing: "border-box",
          p: { xs: 1.25, sm: 2.5, md: 3 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.25, sm: 2.5, md: 3 },
            mb: { xs: 1.5, sm: 2.5 },
            borderRadius: { xs: 2, sm: 3 },
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.14 : 0.07)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 55%)`,
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          <Stack spacing={{ xs: 1.25, sm: 2 }} sx={{ width: "100%", minWidth: 0 }}>
            <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0, width: "100%" }}>
              <Box
                sx={{
                  width: { xs: 36, sm: 52 },
                  height: { xs: 36, sm: 52 },
                  borderRadius: { xs: 2, sm: 2.5 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.14),
                  flexShrink: 0,
                }}
              >
                <AssignmentOutlinedIcon color="primary" fontSize={isMobile ? "small" : "medium"} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant={isMobile ? "h6" : "h4"}
                  component="h1"
                  sx={{ fontWeight: 800, lineHeight: 1.15 }}
                >
                  {inboxCopy.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.75,
                    lineHeight: 1.65,
                    maxWidth: 720,
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  {inboxCopy.intro}
                </Typography>
                {!isAnalytics && !isLoading && !isError ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: { xs: 0.75, sm: 1.5 } }}>
                    {(stats.open ?? 0) > 0 || groups.waiting.length === 0 ? (
                      <Chip
                        size="small"
                        color="primary"
                        label={t("inboxStatOpen", { count: stats.open ?? 0 })}
                        sx={{ fontWeight: 700 }}
                      />
                    ) : null}
                    {(stats.overdue ?? 0) > 0 ? (
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        icon={<WarningAmberOutlinedIcon />}
                        label={t("inboxStatOverdue", { count: stats.overdue })}
                        sx={{ fontWeight: 700 }}
                      />
                    ) : null}
                  </Stack>
                ) : null}
              </Box>
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{
                width: "100%",
                minWidth: 0,
                justifyContent: "flex-start",
                "& .MuiButton-root": {
                  minWidth: 0,
                  flex: { xs: "1 1 auto", sm: "0 1 auto" },
                },
              }}
            >
              {showBackToBoards ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate(boardsPath)}
                  sx={{ fontWeight: 600 }}
                >
                  {t("inboxBackToBoards")}
                </Button>
              ) : null}
              {isMobile ? (
                <Tooltip title={t("refreshList")}>
                  <IconButton
                    size="small"
                    onClick={() => refetch()}
                    disabled={isLoading || isFetching}
                    aria-label={t("refreshList")}
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1.5,
                      width: 36,
                      height: 36,
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => refetch()}
                  disabled={isLoading || isFetching}
                  sx={{ fontWeight: 600 }}
                >
                  {t("refreshList")}
                </Button>
              )}
              {showOpenInPopup ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNewOutlinedIcon />}
                  onClick={() =>
                    openTeamTasksPopupWindow({ inbox: true, scope: scopeParam })
                  }
                  sx={{ fontWeight: 600 }}
                >
                  {t("openInPopup")}
                </Button>
              ) : null}
            </Stack>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            px: { xs: 1, sm: 2 },
            py: { xs: 1, sm: 1.25 },
            mb: { xs: 1.5, sm: 2.5 },
            borderRadius: { xs: 2, sm: 2.5 },
            border: `1px solid ${alpha(theme.palette.divider, 0.75)}`,
            bgcolor: alpha(theme.palette.background.paper, 0.85),
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          <Stack spacing={1.25} sx={{ width: "100%", minWidth: 0 }}>
            <Tabs
              value={scopeParam}
              onChange={(_, v) => setScope(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                minHeight: 40,
                maxWidth: "100%",
                minWidth: 0,
                "& .MuiTabs-flexContainer": { gap: 0.5 },
              }}
            >
              <Tab value="me" label={t("inboxTabMine")} sx={{ fontWeight: 700, minHeight: 40, px: 1.5 }} />
              <Tab value="created" label={t("inboxTabCreated")} sx={{ fontWeight: 700, minHeight: 40, px: 1.5 }} />
              {canManageTeam ? (
                <Tab value="team" label={t("inboxTabTeam")} sx={{ fontWeight: 700, minHeight: 40, px: 1.5 }} />
              ) : null}
              <Tab value="analytics" label={t("inboxTabAnalytics")} sx={{ fontWeight: 700, minHeight: 40, px: 1.5 }} />
            </Tabs>

            {isAnalytics ? null : (
            <>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ width: "100%", minWidth: 0 }}
            >
                <Chip
                  label={t("inboxFilterOpen")}
                  clickable
                  size="small"
                  color={statusParam === "open" && !overdueOnly && !dueParam ? "primary" : "default"}
                  variant={statusParam === "open" && !overdueOnly && !dueParam ? "filled" : "outlined"}
                  onClick={() => setOpenStatusFilter()}
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label={t("inboxFilterOverdue")}
                  clickable
                  size="small"
                  color={overdueOnly ? "warning" : "default"}
                  variant={overdueOnly ? "filled" : "outlined"}
                  onClick={() => setOpenStatusFilter({ overdue: !overdueOnly })}
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label={t("inboxFilterDueToday")}
                  clickable
                  size="small"
                  color={dueParam === DUE_FILTERS.TODAY ? "primary" : "default"}
                  variant={dueParam === DUE_FILTERS.TODAY ? "filled" : "outlined"}
                  onClick={() =>
                    setOpenStatusFilter({
                      due: dueParam === DUE_FILTERS.TODAY ? null : DUE_FILTERS.TODAY,
                    })
                  }
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label={t("inboxFilterDueWeek")}
                  clickable
                  size="small"
                  color={dueParam === DUE_FILTERS.WEEK ? "primary" : "default"}
                  variant={dueParam === DUE_FILTERS.WEEK ? "filled" : "outlined"}
                  onClick={() =>
                    setOpenStatusFilter({
                      due: dueParam === DUE_FILTERS.WEEK ? null : DUE_FILTERS.WEEK,
                    })
                  }
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label={t("inboxFilterDueNone")}
                  clickable
                  size="small"
                  color={dueParam === DUE_FILTERS.NONE ? "primary" : "default"}
                  variant={dueParam === DUE_FILTERS.NONE ? "filled" : "outlined"}
                  onClick={() =>
                    setOpenStatusFilter({
                      due: dueParam === DUE_FILTERS.NONE ? null : DUE_FILTERS.NONE,
                    })
                  }
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label={t("inboxFilterRequired")}
                  clickable
                  size="small"
                  color={requiredOnly ? "warning" : "default"}
                  variant={requiredOnly ? "filled" : "outlined"}
                  onClick={() => setRequiredFilter(!requiredOnly)}
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label={t("inboxFilterDone")}
                  clickable
                  size="small"
                  color={statusParam === "done" ? "success" : "default"}
                  variant={statusParam === "done" ? "filled" : "outlined"}
                  onClick={() => {
                    patchSearchParams((next) => {
                      next.set("status", "done");
                      next.delete("overdue");
                      next.delete("due");
                    });
                  }}
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label={t("inboxFilterAll")}
                  clickable
                  size="small"
                  variant={statusParam === "all" && !overdueOnly && !dueParam ? "filled" : "outlined"}
                  onClick={() => {
                    patchSearchParams((next) => {
                      next.set("status", "all");
                      next.delete("overdue");
                      next.delete("due");
                    });
                  }}
                  sx={{ fontWeight: 600 }}
                />
              </Stack>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ width: "100%", minWidth: 0 }}
            >
              {showAssigneeFilter ? (
                <Autocomplete
                  options={assigneeOptions}
                  value={selectedUser}
                  onChange={(_, user) => setAssigneeFilter(user)}
                  getOptionLabel={(u) => u?.nome || u?.name || u?.email || ""}
                  isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label={t("inboxFilterAssignee")}
                      placeholder={t("inboxFilterAssigneePlaceholder")}
                    />
                  )}
                  sx={{ width: "100%", minWidth: 0, maxWidth: { sm: 360 } }}
                />
              ) : null}
              <Autocomplete
                options={clientOptions}
                value={selectedClient}
                onChange={(_, client) => setClientFilter(client)}
                getOptionLabel={(c) => c?.name || ""}
                isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={t("inboxFilterClient")}
                    placeholder={t("inboxFilterClientPlaceholder")}
                  />
                )}
                sx={{ width: "100%", minWidth: 0, maxWidth: { sm: 360 } }}
              />
            </Stack>
            </>
            )}
          </Stack>
        </Paper>

        {isAnalytics ? (
          <MyTasksAnalytics />
        ) : null}

        {!isAnalytics && isLoading ? <InboxSkeleton /> : null}
        {!isAnalytics && isError ? <TeamTasksLoadError error={error} onRetry={() => refetch()} /> : null}

        {!isAnalytics && !isLoading && !isError && tasks.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: 3,
              border: `1px dashed ${alpha(theme.palette.divider, 0.9)}`,
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            <Typography variant="body1" fontWeight={600} gutterBottom>
              {t("inboxEmptyTitle")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.6, maxWidth: 480, mx: "auto", wordBreak: "break-word" }}
            >
              {inboxCopy.empty}
            </Typography>
          </Paper>
        ) : null}

        {!isAnalytics && !isLoading && !isError && tasks.length > 0 ? (
          <Stack spacing={3} sx={{ width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}>
            {inboxSections.map((section) => (
              <TaskGroupSection
                key={section.key}
                title={section.title}
                subtitle={section.subtitle}
                tone={section.tone}
                tasks={section.tasks}
                showAssignees={showAssignees}
                showBoard={showBoard}
                showColumnStatus={showColumnStatus}
                onOpenTask={handleOpenTask}
                onMoveTask={handleMoveTask}
                canExecuteTask={canExecuteTask}
                t={t}
              />
            ))}
          </Stack>
        ) : null}
      </Box>

      <TaskCardModalContainer
        open={modalOpen}
        card={selectedTask}
        boardId={selectedTask?.boardId}
        onClose={handleCloseModal}
        invalidateInboxOnClose
      />
    </Box>
  );
};

const TasksInboxPage = (props) => (
  <TeamTasksFeedbackProvider>
    <TasksInboxPageInner {...props} />
  </TeamTasksFeedbackProvider>
);

export default TasksInboxPage;
